// project imports
import { alpha, Theme } from '@mui/material/styles';

// types
import { ThemeMode } from '@app/types/config';

export default function componentStyleOverrides(
  theme: Theme,
  borderRadius: number
) {
  const { mode } = theme.palette;
  const menuSelectedBack =
    mode === ThemeMode.DARK
      ? alpha(theme.palette.secondary.main, 0.15)
      : theme.palette.secondary.light;
  const menuSelected =
    mode === ThemeMode.DARK
      ? theme.palette.secondary.main
      : theme.palette.secondary.dark;

  return {
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: theme.palette.grey[800],
          fontWeight: 400,
          '& .MuiFormLabel-asterisk': {
            fontSize: '14px',
            color: theme.palette.error[500],
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: '8px',
          minHeight: '40px',
          minWidth: '40px',
          textTransform: 'none',
          variants: [],
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        rounded: {
          borderRadius: `${borderRadius}px`,
        },
      },
    },
    MuiCardHeader: {
      styleOverrides: {
        root: {
          color: theme.palette.text.dark,
          padding: '24px',
        },
        title: {
          fontSize: '1.125rem',
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },
    MuiCardActions: {
      styleOverrides: {
        root: {
          padding: '24px',
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          alignItems: 'center',
        },
        outlined: {
          border: '1px dashed',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          paddingTop: '10px',
          paddingBottom: '10px',
          '&.Mui-selected': {
            color: menuSelected,
            backgroundColor: menuSelectedBack,
            '&:hover': {
              backgroundColor: menuSelectedBack,
            },
            '& .MuiListItemIcon-root': {
              color: menuSelected,
            },
          },
          '&:hover': {
            backgroundColor: menuSelectedBack,
            color: menuSelected,
            '& .MuiListItemIcon-root': {
              color: menuSelected,
            },
          },
        },
      },
    },
    MuiListItemIcon: {
      styleOverrides: {
        root: {
          color: theme.palette.text.primary,
          minWidth: '36px',
        },
      },
    },
    MuiListItemText: {
      styleOverrides: {
        primary: {
          color: theme.palette.text.dark,
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: {
          padding: '14px 13px',
        },
        input: {
          color: theme.palette.text.dark,
          '&::placeholder': {
            color: '#98a2b3',
            fontSize: '0.875rem',
            opacity: 1,
            fontWeight: 400,
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          [theme.breakpoints.down('lg')]: {
            padding: '10px',
          },
          background: theme.palette.background.paper,
          borderRadius: `${borderRadius}px`,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor:
              mode === ThemeMode.DARK
                ? alpha(theme.palette.text.primary, 0.28)
                : '#d0d5dd',
          },
          '&:hover $notchedOutline': {
            borderColor: theme.palette.primary.light,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
          },
          '&.MuiInputBase-multiline': {
            padding: 1,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: theme.palette.primary.main,
            borderWidth: '1px',
            boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.2)}`,
          },
        },
        input: {
          fontWeight: 500,
          background: theme.palette.background.paper,
          padding: '0',
          paddingLeft: '4px',
          outline: 'none',
          borderRadius: `${borderRadius}px`,
          '&.MuiInputBase-inputSizeSmall': {
            padding: '10px 14px',
            '&.MuiInputBase-inputAdornedStart': {
              paddingLeft: 0,
            },
          },
        },
        inputAdornedStart: {
          paddingLeft: 4,
        },
        notchedOutline: {
          borderRadius: `${borderRadius}px`,
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          '&.Mui-disabled': {
            color:
              mode === ThemeMode.DARK
                ? alpha(theme.palette.text.primary, 0.5)
                : theme.palette.grey[300],
          },
        },
        mark: {
          backgroundColor: theme.palette.background.paper,
          width: '4px',
        },
        valueLabel: {
          color:
            mode === ThemeMode.DARK
              ? theme.palette.primary.main
              : theme.palette.primary.light,
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          '& .MuiAutocomplete-tag': {
            background:
              mode === ThemeMode.DARK
                ? alpha(theme.palette.text.primary, 0.2)
                : theme.palette.secondary.light,
            borderRadius: 4,
            color: theme.palette.text.dark,
            '.MuiChip-deleteIcon': {
              color:
                mode === ThemeMode.DARK
                  ? alpha(theme.palette.text.primary, 0.8)
                  : theme.palette.secondary[200],
            },
          },
          '& .MuiAutocomplete-inputRoot': {
            height: 'max-content',
          },
        },
        popper: {
          borderRadius: `${borderRadius}px`,
          boxShadow:
            '0px 8px 10px -5px rgb(0 0 0 / 20%), 0px 16px 24px 2px rgb(0 0 0 / 14%), 0px 6px 30px 5px rgb(0 0 0 / 12%)',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: theme.palette.divider,
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          padding: '10px 14px',
          '&:focus': {
            backgroundColor: 'transparent',
          },
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          color:
            mode === ThemeMode.DARK
              ? theme.palette.dark.main
              : theme.palette.primary.dark,
          background:
            mode === ThemeMode.DARK
              ? theme.palette.text.primary
              : theme.palette.primary[200],
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          '&.MuiChip-deletable .MuiChip-deleteIcon': {
            color: 'inherit',
          },
        },
      },
    },
    MuiTimelineContent: {
      styleOverrides: {
        root: {
          color: theme.palette.text.dark,
          fontSize: '16px',
        },
      },
    },
    MuiTreeItem: {
      styleOverrides: {
        label: {
          marginTop: 14,
          marginBottom: 14,
        },
      },
    },
    MuiTimelineDot: {
      styleOverrides: {
        root: {
          boxShadow: 'none',
        },
      },
    },
    MuiInternalDateTimePickerTabs: {
      styleOverrides: {
        tabs: {
          backgroundColor:
            mode === ThemeMode.DARK
              ? theme.palette.dark[900]
              : theme.palette.primary.light,
          '& .MuiTabs-flexContainer': {
            borderColor:
              mode === ThemeMode.DARK
                ? alpha(theme.palette.text.primary, 0.2)
                : theme.palette.primary[200],
          },
          '& .MuiTab-root': {
            color:
              mode === ThemeMode.DARK
                ? theme.palette.text.secondary
                : theme.palette.grey[900],
          },
          '& .MuiTabs-indicator': {
            backgroundColor: theme.palette.primary.dark,
          },
          '& .Mui-selected': {
            color: theme.palette.primary.dark,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        flexContainer: {
          borderBottom: '1px solid',
          borderColor:
            mode === ThemeMode.DARK
              ? alpha(theme.palette.text.primary, 0.2)
              : theme.palette.grey[200],
        },
        scrollButtons: {
          '&.Mui-disabled': {
            opacity: 0.3,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          padding: '12px 0 12px 0',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          backgroundColor: 'white',
          border: '1px solid',
          borderColor: theme.palette.divider,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: theme.palette.grey[50],
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderColor:
            mode === ThemeMode.DARK
              ? alpha(theme.palette.text.primary, 0.15)
              : theme.palette.grey[200],
          '&.MuiTableCell-head': {
            fontSize: '12px',
            color:
              mode === ThemeMode.DARK
                ? theme.palette.grey[600]
                : theme.palette.grey[400],
            fontWeight: 600,
            textWrap: 'nowrap',
            backgroundColor: theme.palette.grey['50'],
          },
        },
        head: {
          height: '40px',
          paddingTop: '0',
          paddingBottom: '0',
        },
      },
    },
    MuiDateTimePickerToolbar: {
      styleOverrides: {
        timeDigitsContainer: {
          alignItems: 'center',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          color: theme.palette.background.paper,
          background: theme.palette.text.primary,
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          fontSize: '1.25rem',
        },
      },
    },
    MuiPaginationItem: {
      styleOverrides: {
        root: {
          margin: '3px',
        },
      },
    },
    MuiDataGrid: {
      defaultProps: {
        rowHeight: 54,
      },
      styleOverrides: {
        root: {
          borderWidth: 0,
          '& .MuiDataGrid-columnHeader--filledGroup': {
            borderBottomWidth: 0,
          },
          '& .MuiDataGrid-columnHeader--emptyGroup': {
            borderBottomWidth: 0,
          },
          '& .MuiFormControl-root>.MuiInputBase-root': {
            backgroundColor: `${theme.palette.background.default} !important`,
            borderColor:
              mode === ThemeMode.DARK
                ? `${alpha(theme.palette.divider, 0.05)} !important`
                : `${theme.palette.divider} !important`,
          },
        },
        toolbarContainer: {
          '& .MuiButton-root': {
            paddingLeft: '16px !important',
            paddingRight: '16px !important',
          },
        },
        withBorderColor: {
          borderBottom: '1px solid',
          borderColor:
            mode === ThemeMode.DARK
              ? alpha(theme.palette.divider, 0.15)
              : theme.palette.divider,
        },
        columnHeader: {
          color: theme.palette.grey[600],
          paddingLeft: 24,
          paddingRight: 24,
        },
        footerContainer: {
          '&.MuiDataGrid-withBorderColor': {
            borderBottom: 'none',
          },
        },
        columnHeaderCheckbox: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        cellCheckbox: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        cell: {
          borderWidth: 0,
          paddingLeft: 24,
          paddingRight: 24,
          '&.MuiDataGrid-cell--withRenderer > div ': {
            ...(mode === ThemeMode.DARK && {
              color: theme.palette.grey[50],
            }),
            ' > .high': {
              background:
                mode === ThemeMode.DARK
                  ? theme.palette.success.dark
                  : theme.palette.success.light,
            },
            '& > .medium': {
              background:
                mode === ThemeMode.DARK
                  ? theme.palette.warning.dark
                  : theme.palette.warning.light,
            },
            '& > .low': {
              background:
                mode === ThemeMode.DARK
                  ? theme.palette.error.dark
                  : theme.palette.error.light,
            },
          },
        },
      },
    },
  };
}
